import type { MillsheetSortConfig } from '@mbfab/shared'
import { useState } from 'react'

import { EMPTY_MILLSHEET_FILTER } from '../millsheet/filter-state'
import type { MillsheetFilterOptions } from '../millsheet/filter-state'
import { MillsheetFilterBar } from '../millsheet/millsheet-filter-bar'
import type { MillsheetSearch } from '../millsheet/millsheets-table'

import { MillsheetItemsTable, OrderNumberCell } from './unmatched-mill-sheet-row'
import type { UnmatchedMillsheetViewModel, UpdateOrderNumberResult } from './unmatched-millsheet-types'

import { DataTableLink } from '@/components/app/data-table-link'
import { ColumnHelp } from '@/components/ui/data-table/column-help'
import type { DataTableColumn, DataTableExpandable, DataTableSorter } from '@/components/ui/data-table/data-table'
import { DataTable } from '@/components/ui/data-table/data-table'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { TooltipProvider } from '@/components/ui/tooltip'

// ミルシート側に登録されている注文番号の意味（v1 MILLSHEET_REFERENCE_NUMBER_HELP を一字一句移植）
const MILLSHEET_ORDER_NUMBER_HELP =
  'ミルシート側に登録されている注文番号（契約番号 / 購買オーダ番号）です。発注データの参照番号と一致すると、その発注に自動で紐付きます。該当する発注がまだ無い場合も入力は保持され、発注データが取り込まれた時点で紐付きます。'

// ページ内だけを並び替え・絞り込むと別ページの一致行を欠落させるため、検索条件とソートは
// onChangeSearch を通してコンテナへ返し、バックエンドへ適用する。
export function UnmatchedMillSheets({
  millsheets,
  millsheetSortConfig,
  filterOptions,
  loading,
  pagination,
  onChangePage,
  search,
  onChangeSearch,
  onUpdateOrderNumber,
}: {
  millsheets: UnmatchedMillsheetViewModel[]
  millsheetSortConfig: MillsheetSortConfig
  filterOptions: MillsheetFilterOptions
  loading: boolean
  pagination: { page: number; pageSize: number; totalCount: number }
  onChangePage: (page: number) => void
  // 絞り込み条件はコンテナが持つ（URL と同期し、一覧の取得条件と同じ値を初回描画から使うため）
  search: MillsheetSearch
  onChangeSearch: (search: MillsheetSearch, sorters: DataTableSorter[]) => void
  onUpdateOrderNumber: (millsheetId: string, orderNumber: string) => Promise<UpdateOrderNumberResult>
}) {
  const { filter, freeWord } = search
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [sorters, setSorters] = useState<DataTableSorter[]>([])

  const toggleExpanded = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const columns: Array<DataTableColumn<UnmatchedMillsheetViewModel>> = [
    {
      key: 'no',
      title: '証明書番号',
      sortable: true,
      fixed: 'left',
      keyColumn: true,
      minWidthClass: 'min-w-52',
      render: (row) => (
        <DataTableLink
          to="/t/$tenant_id/millsheets/$millsheet_id"
          params={{ tenant_id: row.tenantId, millsheet_id: row.id }}
        >
          {row.no}
        </DataTableLink>
      ),
    },
    {
      key: 'orderNumber',
      title: '注文番号（契約番号 / オーダー番号）',
      help: <ColumnHelp title="注文番号" help={MILLSHEET_ORDER_NUMBER_HELP} />,
      sortable: true,
      minWidthClass: 'min-w-72',
      render: (row) => <OrderNumberCell row={row} onUpdateOrderNumber={onUpdateOrderNumber} />,
    },
    {
      key: 'constructionName',
      title: '工事名称',
      sortable: true,
      minWidthClass: 'min-w-56',
      render: (row) => (
        <span className="block max-w-64 truncate" title={row.constructionName}>
          {valueOrDash(row.constructionName)}
        </span>
      ),
    },
    {
      key: 'tradingCompany',
      title: 'サプライヤー名',
      sortable: true,
      minWidthClass: 'min-w-44',
      render: (row) => <span className="whitespace-nowrap">{valueOrDash(row.tradingCompany)}</span>,
    },
    {
      key: 'manufacturerName',
      title: '製造会社名',
      sortable: true,
      minWidthClass: 'min-w-40',
      render: (row) => <span className="whitespace-nowrap">{valueOrDash(row.manufacturerName)}</span>,
    },
    {
      key: 'productNames',
      title: '品名',
      minWidthClass: 'min-w-36',
      render: (row) => (
        <span className="block max-w-40 truncate" title={row.productNames}>
          {valueOrDash(row.productNames)}
        </span>
      ),
    },
    {
      key: 'steelTypes',
      title: '規格',
      minWidthClass: 'min-w-28',
      render: (row) => <span className="whitespace-nowrap">{valueOrDash(row.steelTypes)}</span>,
    },
    {
      key: 'totalWeight',
      title: '合計重量(kg)',
      numeric: true,
      minWidthClass: 'min-w-36',
      render: (row) => <span className="font-medium tabular-nums">{row.totalWeight.toLocaleString()}</span>,
    },
    {
      key: 'issuedDate',
      title: '発行日',
      sortable: true,
      minWidthClass: 'min-w-32',
      render: (row) => <span className="whitespace-nowrap tabular-nums">{row.issuedDate}</span>,
    },
  ]

  const expandable: DataTableExpandable<UnmatchedMillsheetViewModel> = {
    isExpanded: (row) => expanded.has(row.id),
    onToggle: (row) => {
      toggleExpanded(row.id)
    },
    expandedRowTitle: (row) => (
      <>
        {row.no} の明細
        <span className="ml-2 text-xs font-medium text-muted-foreground">{row.items.length}件</span>
      </>
    ),
    expandedRowRender: (row) => <MillsheetItemsTable row={row} />,
    // 展開トグルの aria-label は従来どおり証明書番号ベースを維持する（rowKey は React key 専用に切り離す）
    rowLabel: (row) => row.no,
  }

  return (
    <TooltipProvider>
      <DataTable
        columns={columns}
        rows={millsheets}
        // 証明書番号（no）は裏書運用で同一値の複数行が正規データとして存在しうるため React key には
        // 使えない（millsheets-table.tsx と同じ理由）。testid は移行前の実装を踏襲し no ベースのまま維持する
        rowKey={(row) => row.id}
        rowTestId={(row) => `unlinked-millsheet-row-${row.no}`}
        testIdPrefix="unlinked-millsheet"
        loading={loading}
        pagination={{ ...pagination, onChangePage }}
        emptyMessage={
          millsheets.length === 0
            ? '未紐付けのミルシートはありません（取り込み済みのミルシートは全て発注に紐付いています）'
            : '該当するミルシートがありません'
        }
        sorters={sorters}
        onChangeSorters={(next) => {
          setSorters(next)
          onChangeSearch({ filter, freeWord }, next)
        }}
        expandable={expandable}
        toolbar={
          <div className="flex w-full flex-col gap-4">
            <div>
              <h2 className="font-semibold text-foreground">未紐付けミルシート</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                注文番号を確認・修正して、発注データとの紐付けを行います。
              </p>
            </div>
            <MillsheetFilterBar
              value={filter}
              onChange={(next) => {
                onChangeSearch({ filter: next, freeWord }, sorters)
              }}
              onClearAll={() => {
                onChangeSearch({ filter: EMPTY_MILLSHEET_FILTER, freeWord: '' }, sorters)
              }}
              freeWord={freeWord}
              onFreeWordChange={(next) => {
                onChangeSearch({ filter, freeWord: next }, sorters)
              }}
              options={filterOptions}
              sortConfig={millsheetSortConfig}
              testIdPrefix="unlinked-millsheet-filter"
              freeWordPlaceholder="注文番号・証明書番号・工事名称・メーカーなどで検索"
              millsheetFilterHeading="未紐付けミルシートを絞り込み"
              variant="unmatched"
            />
          </div>
        }
        banners={
          <div className="flex min-h-12 items-center border-b border-border px-4">
            <p className="text-sm text-muted-foreground" data-testid="unlinked-millsheet-result-count">
              全 <span className="font-medium text-foreground">{pagination.totalCount}</span> 件
            </p>
            <p className="ml-auto text-xs text-muted-foreground">注文番号は一覧から直接編集できます</p>
          </div>
        }
      />
    </TooltipProvider>
  )
}
